import React, { useRef, useEffect, useState } from "react";
import { useAppContext } from "../../stores/appStore";
import "./StarsAnimation.css";

const StarsAnimation = () => {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const animationFrameRef = useRef(null);
  const smoothTransitionRef = useRef(null); // ✅ FIX 1: Track smooth transition loop
  // ✅ FIX 2: Reuse canvas context
  const ctxRef = useRef(null);

  const { altitudeAnimationInProgress } = useAppContext();

  const [mx, setMx] = useState(0.75);
  const [my, setMy] = useState(0.6);
  const [targetMx, setTargetMx] = useState(0.75);
  const [targetMy, setTargetMy] = useState(0.6);

  // Smooth transition effect for movement speed
  useEffect(() => {
    let animationFrameId;

    const smoothTransition = () => {
      setMx((prevMx) => prevMx + (targetMx - prevMx) * 0.001);
      setMy((prevMy) => prevMy + (targetMy - prevMy) * 0.001);
      animationFrameId = requestAnimationFrame(smoothTransition);
    };

    animationFrameId = requestAnimationFrame(smoothTransition);
    smoothTransitionRef.current = animationFrameId; // ✅ FIX 1: Track it

    // ✅ FIX 1: Better cleanup
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [targetMx, targetMy]);

  // Update target speeds based on altitude animation
  useEffect(() => {
    if (altitudeAnimationInProgress) {
      setTargetMx(0.75);
      setTargetMy(0.6);
    } else {
      setTargetMx(0.75);
      setTargetMy(0.6);
    }
  }, [altitudeAnimationInProgress]);

  // ✅ FIX 3: CRITICAL - Memoize the effect dependencies to prevent constant recreation
  // Store current mx/my in refs to avoid triggering the effect
  const mxMemoRef = useRef(mx);
  const myMemoRef = useRef(my);

  useEffect(() => {
    mxMemoRef.current = mx;
    myMemoRef.current = my;
  }, [mx, my]);

  // Initialize stars - ONLY ONCE
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ✅ FIX 2: Reuse canvas context
    if (!ctxRef.current) {
      ctxRef.current = canvas.getContext("2d");
    }
    const ctx = ctxRef.current;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Generate initial star positions - ONLY if empty
    if (starsRef.current.length === 0) {
      const generateInitialStar = () => ({
        x: (Math.random() - 0.5) * canvas.width * 2,
        y: (Math.random() - 0.5) * canvas.height * 2,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.5,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
        twinklePhase: Math.random() * Math.PI * 2,
      });
      starsRef.current = Array.from({ length: 600 }, generateInitialStar);
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ✅ FIX 3: Use ref values instead of state values
      const currentMx = mxMemoRef.current;
      const currentMy = myMemoRef.current;

      starsRef.current.forEach((star) => {
        // Update position based on movement speed
        star.x -= currentMx;
        star.y += currentMy;

        // Reset star position when it goes off screen
        if (star.x < -canvas.width || star.x > canvas.width * 2) {
          star.x = (Math.random() - 0.5) * canvas.width * 2 + canvas.width;
        }
        if (star.y < -canvas.height || star.y > canvas.height * 2) {
          star.y = (Math.random() - 0.5) * canvas.height * 2 + canvas.height;
        }

        // Update twinkle effect
        star.twinklePhase += star.twinkleSpeed;
        const twinkle = Math.sin(star.twinklePhase) * 0.3 + 0.7;

        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * twinkle})`;
        ctx.fill();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);

    // ✅ FIX 4: Better cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      window.removeEventListener("resize", handleResize);
      if (ctx && canvas) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, []); // ✅ FIX 3: EMPTY DEPS - only run once!

  // ✅ FIX 5: Cleanup on unmount
  useEffect(() => {
    return () => {
      if (smoothTransitionRef.current) {
        cancelAnimationFrame(smoothTransitionRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className="stars-canvas" />;
};

export default StarsAnimation;
